/**
 * Mappers para convertir entre DTOs y modelos de dominio de Notification
 */

import { NotificationDto } from '@infra/dto/notification.dto';
import { Notification } from '@domain/models/notification.model';

export class NotificationMapper {
  /**
   * Parsea una fecha de forma segura, retornando undefined si no es válida
   */
  private static parseDate(dateValue: string | undefined): Date | undefined {
    if (!dateValue) return undefined;
    try {
      const parsed = new Date(dateValue);
      return isNaN(parsed.getTime()) ? undefined : parsed;
    } catch {
      return undefined;
    }
  }

  /**
   * Convierte un DTO NotificationDto a modelo de dominio Notification
   * Normaliza diferentes formatos de respuesta del backend
   */
  static toDomain(dto: NotificationDto): Notification {
    // Extraer ID desde diferentes propiedades posibles
    // Usar notación de corchetes para propiedades del index signature
    const id = String(
      dto.notificationId ?? 
      dto.id ?? 
      dto['_id'] ?? 
      dto['uuid'] ?? 
      ''
    );

    return {
      id,
      contractId: dto.contractId || dto.contract_id,
      contractorId: dto.contractorId || dto.contractor_id,
      quoteId: dto.quoteId || dto.quote_id || dto.data?.quoteId,
      projectId: dto.projectId || dto.project_id || dto.data?.projectId,
      title: dto.title || dto.subject || dto.name,
      body: dto.body || dto.message || dto.description,
      message: dto.message || dto.body || dto.description,
      data: dto.data || dto.payload,
      isRead: (dto.isRead ?? dto.read) ? true : false,
      createdAt: this.parseDate(dto.createdAt || dto.created_at || dto.created),
      updatedAt: this.parseDate(dto.updatedAt || dto.updated_at || dto.updated)
    };
  }

  /**
   * Convierte una lista de DTOs a modelos de dominio
   */
  static toDomainList(dtos: NotificationDto[]): Notification[] {
    return dtos.map(dto => NotificationMapper.toDomain(dto));
  }

  /**
   * Convierte un modelo de dominio Notification a DTO
   */
  static toDto(notification: Notification): NotificationDto {
    return {
      id: notification.id,
      notificationId: notification.id,
      contractId: notification.contractId,
      contractorId: notification.contractorId,
      quoteId: notification.quoteId,
      title: notification.title,
      body: notification.body,
      message: notification.message,
      data: notification.data,
      read: notification.isRead,
      isRead: notification.isRead,
      createdAt: notification.createdAt?.toISOString(),
      updatedAt: notification.updatedAt?.toISOString()
    };
  }
}

